import { CostCenter } from 'src/shared/models/cost-center.model';
import { Injectable } from '@angular/core';
import { JobTitle } from 'src/shared/models/job-title.model';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { StaffingGridType, StaffingGridConstants } from './staffing-grid-constants.util';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';

@Injectable()
export class StaffingGridUtil {	 
    static readonly hoursInDay = 24;

    static setPartialShiftBlocks(shiftSchedule: ShiftSchedule) {
        if (!shiftSchedule) {
            return;
        }

        // Blocks currently being edited should be the main source
        if (shiftSchedule.partialShiftBlocks && shiftSchedule.partialShiftBlocks.length) {
            return shiftSchedule.partialShiftBlocks;
        }

        // If no changes are in progress for a published set of shift blocks, use those
        if (shiftSchedule.publishedShiftBlocks && shiftSchedule.publishedShiftBlocks.length) {
            shiftSchedule.partialShiftBlocks = shiftSchedule.publishedShiftBlocks;
            return shiftSchedule.partialShiftBlocks;
        }
    
        // If a set of changes was recently inactivated, use those.
        if (shiftSchedule.inactiveShiftBlocks && shiftSchedule.inactiveShiftBlocks.length) {
            let lastInactiveDate = shiftSchedule.inactiveShiftBlocks.reduce((prev, curr) => prev > curr.inactivated ? prev : curr.inactivated, new Date(0));
            shiftSchedule.partialShiftBlocks = shiftSchedule.inactiveShiftBlocks.filter(block => block.inactivated === lastInactiveDate);
            return shiftSchedule.partialShiftBlocks;
        }
    
        // Brand new cost centers / cost centers without schedules need us to build the blocks for them.
        if (!shiftSchedule.partialShiftBlocks || !shiftSchedule.partialShiftBlocks.length) {
            shiftSchedule.partialShiftBlocks = this.buildShiftBlocks(shiftSchedule);
        }

        // Whichever we select should be the partial shift blocks, as we are now editing them.
        return shiftSchedule.partialShiftBlocks;
    }

    static buildShiftBlocks(shiftSchedule: ShiftSchedule): ShiftBlock[] {
        let shiftBlocks: ShiftBlock[] = [];
    
        if (shiftSchedule.startHour === undefined) {
            return shiftBlocks;
        }
    
        let startHour = shiftSchedule.startHour;
        let endHour = shiftSchedule.endHour;
        
        if (!shiftSchedule.endHour && shiftSchedule.endHour !== 0) {
            endHour = startHour;
        }
    
        if (endHour <= startHour) {
            endHour += this.hoursInDay;
        }
    
        let lengthOfWorkDay = endHour - startHour;
        let blockCount = Math.ceil(lengthOfWorkDay / shiftSchedule.blockLength);
    
        for (let counter = 0; counter < blockCount; counter++) {
            let shiftBlock = new ShiftBlock();
    
            shiftBlock.endHour = Math.min(endHour, (startHour + (shiftSchedule.blockLength * (counter + 1)))) % this.hoursInDay;
            shiftBlock.isWeekday = shiftSchedule.isWeekday;
            shiftBlock.isWeekend = shiftSchedule.isWeekend;
            shiftBlock.isSaturday = shiftSchedule.isSaturday;
            shiftBlock.isSunday = shiftSchedule.isSunday;
            shiftBlock.startHour = (startHour + (shiftSchedule.blockLength * counter)) % this.hoursInDay;
            shiftBlock.id = shiftBlock.startHour * -1;
            shiftBlock.staffingGrid = {};
    
            shiftBlocks.push(shiftBlock);
        }
    
        return shiftBlocks;
    }

    static getGridTypeDescription(gridType: StaffingGridType): string {
        switch(gridType) {
            case StaffingGridType.AllWeekFixed:
                return StaffingGridConstants.AllWeekFixed;
            case StaffingGridType.AllWeekVariable:
                return StaffingGridConstants.AllWeekVariable;
            case StaffingGridType.SaturdayFixed:
                return StaffingGridConstants.SaturdayFixed;
            case StaffingGridType.SaturdayVariable:
                return StaffingGridConstants.SaturdayVariable;
            case StaffingGridType.SundayFixed:
                return StaffingGridConstants.SundayFixed;
            case StaffingGridType.SundayVariable:
                return StaffingGridConstants.SundayVariable;
            case StaffingGridType.WeekdayFixed:
                return StaffingGridConstants.WeekdayFixed;
            case StaffingGridType.WeekdayVariable:
                return StaffingGridConstants.WeekdayVariable;
            case StaffingGridType.WeekendFixed:
                return StaffingGridConstants.WeekendFixed;
            case StaffingGridType.WeekendVariable:
                return StaffingGridConstants.WeekendVariable;
        }
    }

    static getGridTypesFromCostCenter(costCenter: CostCenter): StaffingGridType[] {
        if (WorkflowPageUtil.hasAcuityConfiguration(costCenter) &&
            WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter)) {
            return [
                StaffingGridType.AllWeekFixed,
                StaffingGridType.AllWeekVariable,
                StaffingGridType.SaturdayFixed,
                StaffingGridType.SundayFixed,
                StaffingGridType.WeekdayFixed,
                StaffingGridType.WeekendFixed
            ];
        }
        else if (WorkflowPageUtil.hasInpatientConfiguration(costCenter)) {
            return [ 
                StaffingGridType.WeekdayFixed,
                StaffingGridType.WeekdayVariable,
                StaffingGridType.WeekendFixed,
                StaffingGridType.WeekendVariable
            ];
        }
        else if (WorkflowPageUtil.hasUosConfiguration(costCenter)) {
            return [
                StaffingGridType.AllWeekFixed,
                StaffingGridType.AllWeekVariable,
                StaffingGridType.SaturdayAddOns,
                StaffingGridType.SaturdayFixed,
                StaffingGridType.SaturdayVariable,
                StaffingGridType.SundayAddOns,
                StaffingGridType.SundayFixed,
                StaffingGridType.SundayVariable,
                StaffingGridType.WeekendAddOns,
                StaffingGridType.WeekendFixed,
                StaffingGridType.WeekendVariable
            ];
        }
    }

    static isGridTypeAllWeek(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.AllWeekAddOns ||
            gridType == StaffingGridType.AllWeekFixed ||
            gridType == StaffingGridType.AllWeekVariable;
    }

    static isGridTypeFixed(gridType: StaffingGridType): boolean {
        return gridType === StaffingGridType.AllWeekFixed ||
            gridType === StaffingGridType.SaturdayFixed ||
            gridType === StaffingGridType.SundayFixed ||
            gridType === StaffingGridType.WeekdayFixed ||
            gridType === StaffingGridType.WeekendFixed;
    }

    static isGridTypeSaturday(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.SaturdayAddOns ||
            gridType == StaffingGridType.SaturdayFixed || 
            gridType == StaffingGridType.SaturdayVariable;
    }

    static isGridTypeSunday(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.SundayAddOns ||
            gridType == StaffingGridType.SundayFixed ||
            gridType == StaffingGridType.SundayVariable;
    }

    static isGridTypeVariable(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.AllWeekVariable ||
            gridType == StaffingGridType.SaturdayVariable ||
            gridType == StaffingGridType.SundayVariable ||
            gridType == StaffingGridType.WeekdayVariable || 
            gridType == StaffingGridType.WeekendVariable;
    }

    static isGridTypeWeekday(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.WeekdayAddOns ||
            gridType == StaffingGridType.WeekdayFixed || 
            gridType == StaffingGridType.WeekdayVariable;
    }

    static isGridTypeWeekend(gridType: StaffingGridType): boolean {
        return gridType == StaffingGridType.WeekendAddOns ||
            gridType == StaffingGridType.WeekendFixed || 
            gridType == StaffingGridType.WeekendVariable;
    }  
    
    static checkAddOnSchedules(schedules: ShiftSchedule[]) {
        schedules.forEach(s => {            
            let shiftBlocks = s.partialShiftBlocks;

            s.isValid = !MiscUtil.checkArray(shiftBlocks) || 
                this.isShiftGridEmpty(shiftBlocks) ||
                this.checkCompleteAddOnGrid(shiftBlocks);
        });    
    }

    static checkCompleteAddOnGrid(shiftBlocks: ShiftBlock[]): boolean {
        return !shiftBlocks.some(s => !MiscUtil.isValidNumber(s.addOns));
    }

    static checkCompleteAcuityGrid(costCenter: CostCenter, shiftSchedule: ShiftSchedule) {
        if (!costCenter || !costCenter.ancillaryConfiguration) {
            return false;
        }

        let jobTitles = costCenter.variableJobTitles;
        let acuityCategories = costCenter.ancillaryConfiguration.acuityCategories;
        let isComplete = true;

        if (!MiscUtil.checkArray(jobTitles) || !MiscUtil.checkArray(acuityCategories)) {
            return false;
        }

        jobTitles.forEach(jobTitle => {
          acuityCategories.forEach(acuityCategory => {
            let ratio = shiftSchedule.staffRatios.find(r => !r.inactiveDate && r.acuityCategoryName == acuityCategory.name && r.jobTitleId == jobTitle.id);

                if (isComplete) {
                    isComplete = isComplete 
                        && ratio
                        && ratio.staffValue !== null 
                        && ratio.staffValue !== undefined
                        && ratio.staffValue >= 0
                        && ratio.uosCount !== null 
                        && ratio.uosCount !== undefined
                        && ratio.uosCount >= 0;
                }                
            });
        });
    
        return isComplete;
    }

    static gridContainsValidJobTitle(jobTitleId: number, shiftBlock: ShiftBlock, isFixed: boolean) {
        let alreadyInGrid = jobTitleId in shiftBlock.staffingGrid;

        if (!alreadyInGrid) {
            return false;
        }

        let jobTitleTargets = shiftBlock.staffingGrid[jobTitleId] || {};
        let containsFixedStaffingTarget = -1 in jobTitleTargets;

        if (isFixed) {
            // Fixed grids should ONLY contain the sentinel value.
            return containsFixedStaffingTarget && Object.keys(jobTitleTargets).length == 1;
        } else {
            // Variable grids should NOT contain the sentinel value.
            return !containsFixedStaffingTarget;
        }
    }
    
    static checkCompleteJobsShiftGrid(
        jobTitles: JobTitle[], 
        schedule: ShiftSchedule, 
        isFixed: boolean,
        maxCensus = 0,
        minCensus = 0) {  
        let isValid = false; 
        let shiftBlocks = schedule.partialShiftBlocks;

        isValid = this.isJobGridEmpty(jobTitles) || 
            this.isShiftGridEmpty(shiftBlocks) || 
            this.isJobShiftGridComplete(jobTitles, shiftBlocks, isFixed, maxCensus, minCensus);                    

        schedule.isValid = schedule.isClosed || isValid;
    }

    static checkTotalUosSchedules(jobTitles: JobTitle[], schedules: ShiftSchedule[]) {        
        schedules.forEach(s => { 
            let staffingGrid = s.staffRatios.filter(r => !r.inactiveDate);
            
            s.isValid = MiscUtil.checkArray(staffingGrid) &&
                !jobTitles.some(j => !staffingGrid.some(g => g.jobTitleId === j.id)) &&                
                !staffingGrid.some(r => !MiscUtil.isValidNumber(r.staffValue) || !MiscUtil.isValidNumber(r.uosCount));
        });
    }

    static isJobShiftGridComplete(
        jobTitles: JobTitle[], 
        shiftBlocks: ShiftBlock[], 
        isFixed: boolean, 
        maxCensus: number,
        minCensus: number) : boolean {  
        if (this.isJobGridEmpty(jobTitles) || this.isShiftGridEmpty(shiftBlocks)) {            
            return false;
        }
           
        if (isFixed) {
            return this.isJobShiftGridCensusComplete(-1, jobTitles, shiftBlocks);
        }
        else {
            let diffCensus = maxCensus - minCensus;
   
            for (let counter = 0; counter <= diffCensus; counter++) {
                if (!this.isJobShiftGridCensusComplete(maxCensus - counter, jobTitles, shiftBlocks)) {
                    return false;
                }
            }

            return true;
        }
    }

    private static isJobShiftGridCensusComplete(census: number,jobTitles: JobTitle[], shiftBlocks: ShiftBlock[]): boolean {
        let isComplete = true;

        jobTitles.forEach(jobTitle => {
            shiftBlocks.forEach(shiftBlock => {                           
                if (isComplete) {
                    isComplete = isComplete 
                        && jobTitle.id in shiftBlock.staffingGrid
                        && census in shiftBlock.staffingGrid[jobTitle.id]
                        && shiftBlock.staffingGrid[jobTitle.id][census] !== null
                        && shiftBlock.staffingGrid[jobTitle.id][census] !== undefined
                        && shiftBlock.staffingGrid[jobTitle.id][census] >= 0;
                }  
            });
        });

        return isComplete;
    } 

    private static isJobGridEmpty(jobTitles: JobTitle[]) : boolean {
        return !MiscUtil.checkArray(jobTitles);
    }

    private static isShiftGridEmpty(shiftBlocks: ShiftBlock[]): boolean {
        return !MiscUtil.checkArray(shiftBlocks);
    }    
}
